import {
    CheckoutFeatureFlags,
    EmailAccountStatusResult,
    EmailPreflightResult,
    EventData,
    PaymentPricingResponse,
    PaymentSheetParamsResponse,
    PurchasedTicketListResponse,
    PromoRequest,
    PublicUserData,
    TicketSelection,
} from "../util/types";
import { client } from "./api/client";
import {
    buildEmailStatusURL,
    buildFeatureFlagsURL,
    buildPaymentQuoteURL,
    buildPublicPaymentQuoteURL,
    buildPaymentSheetURL,
    buildPublicEventURL,
    buildPublicEventTicketsURL,
    buildTicketsURL,
    buildUserURL,
} from "./api/urlBuilder";

type CheckoutPayload = {
    eventId: string;
    ticketTypes: TicketSelection[];
    userId: string;
    promo?: PromoRequest;
    refBy?: string | null;
};

type UpdateCheckoutUserPayload = {
    uid: string;
    email?: string;
    name?: string;
    lastName?: string;
};

export type CheckoutAttemptMarker = {
    eventId: string;
    userId: string;
    ticketCount: number;
    startedAt: number;
    ticketTypes: TicketSelection[];
};

const CHECKOUT_ATTEMPT_STORAGE_KEY = 'publicCheckout:lastAttempt';

export async function getPublicEvent(eventId: string): Promise<EventData> {
    const response = await client.get(buildPublicEventURL(eventId));
    const event = response.data.event as EventData;

    if (Array.isArray(event?.tickets) && event.tickets.length > 0) {
        return event;
    }

    try {
        const ticketsResponse = await client.get(buildPublicEventTicketsURL(eventId));
        const tickets = Array.isArray(ticketsResponse?.data?.tickets)
            ? ticketsResponse.data.tickets
            : [];

        return {
            ...event,
            tickets,
        } as EventData;
    } catch {
        return {
            ...event,
            tickets: [],
        } as EventData;
    }
}

export async function getPublicUserData(uid: string, token: string): Promise<PublicUserData | null> {
    const response = await client.get(buildUserURL({ uid }), { token });
    const data = response.data as PublicUserData | { user?: PublicUserData };

    if (data && 'uid' in data && data.uid) {
        return data;
    }

    if (data && typeof data === 'object' && 'user' in data && data.user?.uid) {
        return data.user;
    }

    return null;
}

export async function updateCheckoutUserContact(payload: UpdateCheckoutUserPayload, token: string) {
    const existingUser = await getPublicUserData(payload.uid, token);

    if (!existingUser) {
        await client.post(buildUserURL({}), payload, { token });
    }

    return client.post(buildUserURL({}) + "/update", payload, { token });
}

export async function getPurchasedTickets(uid: string, token: string, limit = 20): Promise<PurchasedTicketListResponse> {
    const response = await client.get(buildTicketsURL({ uid, limit }), { token });
    return (response.data ?? { tickets: [] }) as PurchasedTicketListResponse;
}

export async function mergeCheckoutUsers({ fromUid, toUid, token }: { fromUid: string; toUid: string; token: string }) {
    const response = await client.post(buildUserURL({}) + "/merge", { fromUid, toUid }, { token });
    return response.data;
}

export async function preflightEmailAddress(email: string): Promise<EmailPreflightResult> {
    const normalizedEmail = email.trim().toLowerCase();

    try {
        const response = await client.post(buildUserURL({}) + "/email-preflight", { email: normalizedEmail });
        const data = response?.data ?? {};
        const status = data?.status === 'valid' || data?.status === 'invalid'
            ? data.status
            : 'unverifiable';

        return {
            ok: true,
            status,
            normalizedEmail: data?.normalizedEmail ?? normalizedEmail,
            reasonCode: data?.reasonCode,
            message: data?.message,
        };
    } catch (error: any) {
        return {
            ok: false,
            status: 'unverifiable',
            normalizedEmail,
            reasonCode: error?.data?.reasonCode ?? 'preflight_network_error',
            message: error?.message ?? 'E-Mail-Domain konnte gerade nicht geprüft werden.',
        };
    }
}

export async function lookupEmailAccountStatus(email: string): Promise<EmailAccountStatusResult> {
    const normalizedEmail = email.trim().toLowerCase();

    try {
        const response = await client.post(buildEmailStatusURL(), { email: normalizedEmail });
        const data = response?.data ?? {};
        const status = data?.status === 'found' || data?.status === 'not_found' || data?.status === 'invalid'
            ? data.status
            : 'error';

        return {
            ok: true,
            status,
            exists: Boolean(data?.exists),
            normalizedEmail: data?.normalizedEmail ?? normalizedEmail,
            providerIds: Array.isArray(data?.providerIds) ? data.providerIds : [],
            message: data?.message,
        };
    } catch (error: any) {
        return {
            ok: false,
            status: 'error',
            exists: false,
            normalizedEmail,
            providerIds: [],
            message: error?.message ?? 'E-Mail-Status konnte gerade nicht geprüft werden.',
        };
    }
}

export async function fetchCheckoutFeatureFlags(token: string): Promise<CheckoutFeatureFlags> {
    const response = await client.get(buildFeatureFlagsURL(), { token });
    return (response.data?.checkout ?? response.data) as CheckoutFeatureFlags;
}

export async function fetchPaymentQuote(payload: CheckoutPayload, token: string): Promise<PaymentPricingResponse> {
    const response = await client.post(buildPaymentQuoteURL(), payload, { token });
    return response.data as PaymentPricingResponse;
}

export async function fetchPublicPaymentQuote(payload: Omit<CheckoutPayload, 'userId'>): Promise<PaymentPricingResponse> {
    const response = await client.post(buildPublicPaymentQuoteURL(), payload);
    return response.data as PaymentPricingResponse;
}

export async function createPaymentSheet(payload: CheckoutPayload, token: string): Promise<PaymentSheetParamsResponse> {
    const response = await client.post(buildPaymentSheetURL(), payload, { token });
    return response.data as PaymentSheetParamsResponse;
}

export function saveCheckoutAttemptMarker(marker: CheckoutAttemptMarker) {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(CHECKOUT_ATTEMPT_STORAGE_KEY, JSON.stringify(marker));
    } catch {
        // Ignore storage failures; checkout can still continue without the success-page PDF shortcut.
    }
}

export function readCheckoutAttemptMarker(): CheckoutAttemptMarker | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as CheckoutAttemptMarker;
        if (!parsed || !parsed.eventId || !parsed.userId || !Array.isArray(parsed.ticketTypes)) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

export function clearCheckoutAttemptMarker() {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
    } catch {
        // Ignore storage failures.
    }
}
